import { useCallback } from "react";
import { Button } from "@chakra-ui/react";
import { auth_google_path } from "@/routes";
import { useAnalytics } from "@/hooks/useAnalytics";

interface GoogleButtonProps {
  text?: string;
  returnTo?: string;
  action?: "sign_in" | "sign_up";
}

const getCsrfToken = (): string | null => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? null;
};

const createHiddenInput = (name: string, value: string): HTMLInputElement => {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  return input;
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <g fill="none" fillRule="evenodd">
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </g>
  </svg>
);

/**
 * Follows Google's branding guidelines for OAuth buttons
 * https://developers.google.com/identity/branding-guidelines
 */
export default function GoogleButton({ text = "Continue with Google", returnTo, action }: GoogleButtonProps) {
  const TRACK = useAnalytics();

  const handleClick = useCallback(() => {
    if (action === "sign_in") {
      TRACK.USER_SIGNED_IN("google");
    } else if (action === "sign_up") {
      TRACK.USER_SIGNED_UP("google");
    }

    // OAuth requires a full page redirect, not an AJAX request
    // We create and submit a form to properly handle CSRF protection
    const csrfToken = getCsrfToken();

    if (!csrfToken) {
      console.error("CSRF token not found. Cannot submit OAuth request.");
      return;
    }

    const form = document.createElement("form");
    form.method = "POST";
    form.action = auth_google_path();

    form.appendChild(createHiddenInput("authenticity_token", csrfToken));

    if (returnTo) {
      form.appendChild(createHiddenInput("return_to", returnTo));
    }

    document.body.appendChild(form);

    try {
      form.submit();
    } finally {
      setTimeout(() => {
        document.body.removeChild(form);
      }, 100);
    }
  }, [returnTo, action, TRACK]);

  return (
    <Button
      onClick={handleClick}
      variant="outline"
      type="button"
      width="full"
      height="12"
      fontSize="md"
      fontWeight="medium"
      borderColor="gray.300"
      _hover={{
        bg: "gray.50",
        borderColor: "gray.400",
      }}
      _active={{
        bg: "gray.100",
      }}
      gap="3"
      aria-label="Sign in with Google"
    >
      <GoogleIcon />
      {text}
    </Button>
  );
}
